import { useState } from 'react';
import { BarChart3, CircleDollarSign, Leaf, Percent } from 'lucide-react';
import { Card, CardContent } from '@/shared/components/ui';
import { SectionHeading, StatCard } from '@/shared/components/SolarSections';
import { whySolarBenefits } from '@/shared/utils/SolarData';

const WhySolarPage = () => {
  const [monthlyBill, setMonthlyBill] = useState(25000);
  const [systemSize, setSystemSize] = useState(20);
  const [tariff, setTariff] = useState(8.5);

  const annualSavings = Math.round(monthlyBill * 12 * 0.78);
  const estimatedCost = systemSize * 55000;
  const paybackYears = Math.max(2.5, estimatedCost / Math.max(annualSavings, 1));
  const annualGeneration = Math.round(systemSize * 1500);

  return (
    <div className="px-4 pt-8 pb-16 sm:px-6 lg:px-8 lg:pt-12 lg:pb-24">
      <div className="mx-auto max-w-7xl space-y-14">
        <SectionHeading
          eyebrow="Why Solar"
          title="Make the energy transition work for your budget and your goals"
          description="Solar can help reduce operating costs, improve sustainability, and unlock government support where applicable."
          align="center"
        />

        <div className="grid gap-6 md:grid-cols-3">
          {whySolarBenefits.map(item => (
            <Card key={item.title} className="border-border-primary/70 bg-background-card/90">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-text-primary">{item.title}</h3>
                <p className="mt-3 text-sm leading-7 text-text-secondary">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <section className="grid gap-6 lg:grid-cols-[1fr_0.95fr] lg:items-start">
          <div className="grid gap-6 sm:grid-cols-2">
            <StatCard value="Low" label="Emissions" description="A cleaner way to power buildings and operations." icon={Leaf} />
            <StatCard value="Strong" label="Cost Control" description="Offset utility inflation with your own generation." icon={CircleDollarSign} />
            <StatCard value="Fast" label="Deployment" description="Rooftop projects can be commissioned quickly." icon={BarChart3} />
            <StatCard value="Real" label="ROI Visibility" description="Track savings and payback with easy-to-understand metrics." icon={Percent} />
          </div>

          <Card className="border-border-primary/70 bg-background-card/90">
            <CardContent className="space-y-6 p-6">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-accent-primary">
                  ROI Calculator UI
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-text-primary">
                  Estimate savings from your solar project
                </h3>
              </div>

              <div className="space-y-5">
                <label className="block space-y-2 text-sm font-medium text-text-primary">
                  Monthly electricity bill: ₹{monthlyBill.toLocaleString()}
                  <input
                    type="range"
                    min="5000"
                    max="200000"
                    step="1000"
                    value={monthlyBill}
                    onChange={event => setMonthlyBill(Number(event.target.value))}
                    className="w-full accent-[color:var(--color-accent-primary)]"
                  />
                </label>

                <label className="block space-y-2 text-sm font-medium text-text-primary">
                  System size: {systemSize} kW
                  <input
                    type="range"
                    min="5"
                    max="500"
                    step="5"
                    value={systemSize}
                    onChange={event => setSystemSize(Number(event.target.value))}
                    className="w-full accent-[color:var(--color-accent-primary)]"
                  />
                </label>

                <label className="block space-y-2 text-sm font-medium text-text-primary">
                  Tariff per unit: ₹{tariff.toFixed(1)}
                  <input
                    type="range"
                    min="5"
                    max="15"
                    step="0.1"
                    value={tariff}
                    onChange={event => setTariff(Number(event.target.value))}
                    className="w-full accent-[color:var(--color-accent-primary)]"
                  />
                </label>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-background-secondary p-4">
                  <p className="text-sm text-text-secondary">Annual savings</p>
                  <p className="mt-2 text-2xl font-semibold text-text-primary">
                    ₹{annualSavings.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-2xl bg-background-secondary p-4">
                  <p className="text-sm text-text-secondary">Estimated payback</p>
                  <p className="mt-2 text-2xl font-semibold text-text-primary">
                    {paybackYears.toFixed(1)} years
                  </p>
                </div>
                <div className="rounded-2xl bg-background-secondary p-4">
                  <p className="text-sm text-text-secondary">System cost estimate</p>
                  <p className="mt-2 text-2xl font-semibold text-text-primary">
                    ₹{estimatedCost.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-2xl bg-background-secondary p-4">
                  <p className="text-sm text-text-secondary">Annual generation</p>
                  <p className="mt-2 text-2xl font-semibold text-text-primary">
                    {annualGeneration.toLocaleString()} kWh
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default WhySolarPage;